/* Sleep-study order backup (owner 10-20): the status of the signed order PDF C-HIM delivers to
   Odessa Sleep Associates (OSA) when Dr. Madhu orders a PSG, CPAP titration or split-night study.

   Server (v2, api/sleep_order_fax/routes.py):
     GET  /v1/{pid}/patient/{patientid}/sleep-order-fax?date=YYYY-MM-DD
          -> {enabled, entries: [{key, study, order_names, order_date, status, method,
                                  fax_master_id, rc_message_id, delivered_at, error_code,
                                  retry_allowed, osa_state}]}
     POST /v1/{pid}/patient/{patientid}/sleep-order-fax/retry   {key}
   Delivery itself happens on the server (a worker, after a short grace period, idempotent per
   patient + appointment + study). care only reads the status and, for a failed delivery the
   server marks retry_allowed, asks for one retry. Nothing here sends or delivers anything. */
import { useCallback, useEffect, useState } from "react";
import api from "../api/api";
import { V2_BASE_URL } from "../api/bases";

const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };

// Owner 10-27: the DME order "Auto CPAP 4\u201320" (EN DASH U+2013) is backed up to OSA too. The
// canonical spelling is shown as is; matching folds every dash variant (normName).
export const AUTO_CPAP_NAME = "Auto CPAP 4\u201320";

// The order names (lookup_master buttons) the server tracks, as normName keys. The server's list
// wins: it answers with each entry's own order_names; this list only decides whether to ask at all.
export const SLEEP_STUDY_NAMES = ["PSG", "CPAP TITRATION", "SPLIT NIGHT STUDY", "AUTO CPAP 4-20"];
// Only C-HIM orders are backed up to OSA.
export const SLEEP_FAX_PRACTICES = ["9928"];

export const POLL_MS = 15000;
export const POLL_MAX_MS = 10 * 60 * 1000;
const BUSY = ["queued", "delivering", "sending"];

// Every dash variant: hyphen-minus, hyphen, non-breaking hyphen, figure / en / em dash, horizontal
// bar, hyphen bullet, minus sign, two/three-em dash, small em dash, small / fullwidth hyphen-minus.
const DASH_RUN = /\s*[-\u2010-\u2015\u2043\u2212\u2e3a\u2e3b\ufe58\ufe63\uff0d]+\s*/g;
// invisible: soft hyphen, zero-width space / joiners, word joiner, BOM
const INVISIBLE = /[\u00ad\u200b-\u200d\u2060\ufeff]/g;

// Comparison key ONLY (never shown): NFKC, dashes -> "-", spaces around a dash dropped, whitespace
// collapsed, upper case. "Auto CPAP 4-20", "auto cpap 4 \u2013 20", "AUTO CPAP 4\u221220" -> "AUTO CPAP 4-20".
export const normName = (s) =>
  String(s || "")
    .normalize("NFKC")
    .replace(INVISIBLE, "")
    .replace(DASH_RUN, "-")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
export const isSleepStudyName = (name) => SLEEP_STUDY_NAMES.includes(normName(name));

// What the order pickers say next to a name C-HIM backs up to OSA. It follows the SERVER's rule
// (review 10-27): the status route lists each enabled study with `tag` = whether the signed-in user's
// click can qualify (Auto CPAP: any provider, and only once AUTO_CPAP is enabled; PSG / titration /
// split night: only the configured sleep provider). The visit rule (the patient's visit that day is
// with the ordering provider) is checked per order; the chip after placing shows what happened.
export const OSA_FAX_HINT = "Will be faxed to OSA";
export const OSA_FAX_HINT_TITLE =
  "C-HIM sends this signed order to Odessa Sleep Associates' fax inbox when you order it at your own visit with the patient today";
export const osaFaxHintFrom = (studies, name) => {
  const n = normName(name);
  if (!n || !Array.isArray(studies)) return null;
  const hit = studies.some(
    (st) => st && st.tag === true && Array.isArray(st.order_names) && st.order_names.map(normName).includes(n)
  );
  return hit ? OSA_FAX_HINT : null;
};

// Universal-search text for the order search routes (v1 + v2 run `content ILIKE '%item%'`, unescaped):
// a dash run becomes "%", so "auto cpap 4-20" also finds the stored "Auto CPAP 4\u201320" (utf8mb3
// general_ci does not equate an en dash with "-"). A literal "%" / "_" typed by the user is dropped.
export const searchPattern = (value) =>
  String(value || "")
    .normalize("NFKC")
    .replace(INVISIBLE, "")
    .replace(/[%_\\]/g, " ")
    .replace(DASH_RUN, "%")
    .replace(/\s+/g, " ")
    .trim();

export const sleepFaxPath = (pid, patientId, date) =>
  `/v1/${encodeURIComponent(pid)}/patient/${encodeURIComponent(patientId)}/sleep-order-fax` +
  (date ? `?date=${encodeURIComponent(date)}` : "");

export function fetchSleepFax(pid, patientId, date) {
  return api.get(sleepFaxPath(pid, patientId, date), V2);
}

export function retrySleepFax(pid, patientId, key) {
  return api.post(
    `/v1/${encodeURIComponent(pid)}/patient/${encodeURIComponent(patientId)}/sleep-order-fax/retry`,
    { key },
    V2
  );
}

// The entry for one order card: same order name, and (when both carry one) the same order date.
export function entryFor(entries, item, date) {
  const name = normName(item?.ordername);
  if (!name || !Array.isArray(entries)) return null;
  const matches = entries.filter(
    (e) =>
      (Array.isArray(e.order_names) ? e.order_names.map(normName) : [normName(e.study)]).includes(name) &&
      (!date || !e.order_date || e.order_date === date)
  );
  return matches.length ? matches[matches.length - 1] : null;
}

// Short chip text per server flag (v2 routes.FLAGS); Retry only where the server allows it.
export const FLAG_TEXT = {
  PROVIDER_NPI_MISSING: "Not faxed: provider NPI missing",
  NO_SLEEP_DX: "Not faxed: no sleep apnea diagnosis on the visit",
  NON_PROVIDER: "Not faxed: ordered by a non-provider account",
};

// What the chip says. tone: "busy" | "ok" | "warn" | "off".
export function chipFor(entry) {
  if (!entry) return null;
  const s = String(entry.status || "");
  if (s === "queued" || s === "delivering") return { text: "Queued for OSA", tone: "busy" };
  if (s === "sending") return { text: "Faxing to OSA...", tone: "busy" };
  if (s === "delivered") {
    const id = entry.fax_master_id ? ` (fax #${entry.fax_master_id})` : "";
    if (entry.osa_state === "filed") return { text: "Filed at OSA", tone: "ok", title: `In OSA's fax inbox${id}; OSA filed it` };
    if (entry.osa_state === "worked") return { text: "Handled at OSA", tone: "ok", title: `In OSA's fax inbox${id}; OSA marked it done` };
    if (entry.osa_state === "removed") return { text: "Removed at OSA", tone: "warn", title: `OSA deleted it from their fax inbox${id}` };
    return { text: "Delivered to OSA inbox", tone: "ok", title: `In OSA's fax inbox${id}` };
  }
  if (s === "sent") {
    const id = entry.rc_message_id ? ` (RingCentral ${entry.rc_message_id})` : "";
    return { text: "Faxed to OSA", tone: "ok", title: `Sent by fax${id}` };
  }
  if (s === "failed") {
    // owner / re-review 10-27: orders the server holds and flags (title = the server's message)
    if (entry.flag && FLAG_TEXT[entry.flag.code]) {
      return { text: FLAG_TEXT[entry.flag.code], tone: "warn", title: entry.flag.message || FLAG_TEXT[entry.flag.code],
        retry: Boolean(entry.retry_allowed) };
    }
    return {
      text: "OSA delivery failed",
      tone: "warn",
      title: entry.error_code ? `Not delivered: ${entry.error_code}` : "Not delivered",
      retry: Boolean(entry.retry_allowed),
    };
  }
  if (s === "cancelled") return { text: "Not sent: order removed", tone: "off" };
  if (s === "blocked") return { text: "OSA backup off", tone: "off" };
  return null;
}

/* Status of the sleep-study orders on one chart day. Asks only for C-HIM, only when the list holds
   a sleep-study order, and polls (every POLL_MS, at most POLL_MAX_MS) only while one is still on
   its way (queued / delivering / sending). */
export function useSleepOrderFax(pid, patientId, date, items) {
  const [state, setState] = useState({ enabled: false, entries: [], studies: [] });
  const [retrying, setRetrying] = useState(null);
  const [round, setRound] = useState(0); // bumped by a retry: restarts the poll loop
  const names = (items || []).filter((i) => isSleepStudyName(i?.ordername)).map((i) => normName(i.ordername));
  const namesKey = [...new Set(names)].sort().join("|");
  const active = SLEEP_FAX_PRACTICES.includes(String(pid)) && Boolean(patientId) && namesKey !== "";

  useEffect(() => {
    // one poll loop per effect run; `run.over` stops exactly this loop (an in-flight request of an
    // older loop never schedules another poll or overwrites a newer answer)
    const run = { over: false, timer: null };
    if (!active) {
      setState({ enabled: false, entries: [], studies: [] });
      return () => {
        run.over = true;
      };
    }
    const started = Date.now();
    const tick = async () => {
      let res;
      try {
        res = await fetchSleepFax(pid, patientId, date);
      } catch (e) {
        return; // the chip is extra information: never an error on the orders screen
      }
      if (run.over) return;
      const next = {
        enabled: Boolean(res?.enabled),
        entries: Array.isArray(res?.entries) ? res.entries : [],
        studies: Array.isArray(res?.studies) ? res.studies : [], // additive 10-27: per-user tag rules
      };
      setState(next);
      // the server picks a qualifying order up during this very GET (idempotent enqueue), so only
      // entries still on their way are polled; a staff member's order never gets an entry
      const pending = next.enabled && next.entries.some((e) => BUSY.includes(e.status));
      if (pending && Date.now() - started < POLL_MAX_MS) run.timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => {
      run.over = true;
      if (run.timer) clearTimeout(run.timer);
    };
  }, [active, pid, patientId, date, namesKey, round]);

  const retry = useCallback(
    async (key) => {
      setRetrying(key);
      try {
        await retrySleepFax(pid, patientId, key);
      } catch (e) {
        // the reload below shows the entry as it is
      } finally {
        setRetrying(null);
        setRound((n) => n + 1);
      }
    },
    [pid, patientId]
  );

  return {
    ...state,
    active,
    retrying,
    retry,
    entryFor: (item) => entryFor(state.entries, item, date),
    hintFor: (name) => (active ? osaFaxHintFrom(state.studies, name) : null),
  };
}
